import { PAGE_SX } from '../components/pageLayout';
import React, { useEffect, useState } from "react";
import { Box, IconButton, InputAdornment, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { format } from 'date-fns';

import Header from "../components/Header";
import PageBanner from '../components/PageBanner';
import CustomColumnMenu from "../components/DataGridCustomColumnMenu";
import DataGridCustomToolbar from "../components/DataGridCustomToolbar";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import CustomPagination from "../components/CustomPagination";
import { useAuthContext } from "../context/auth";
import { usePathContext } from "../context/path";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { useVPNConnectLogContext } from "../context/vpnConnectLog";
import FlexBetween from "../components/FlexBetween";
import { Search } from "@mui/icons-material";
import useGridStyles from "../hooks/useGridStyles";

const VPNConnectLog = () => {
  // theme
  const theme = useTheme();
  const { gridSx, gridBorder } = useGridStyles();
  const { auth } = useAuthContext();
  const { setTitle } = usePathContext()
  const { vpnConnectLogs, dispatch } = useVPNConnectLogContext()
  const axiosPrivate = useAxiosPrivate()
  const [ query, setQuery ] = useState("")

  // values for backend
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [sort, setSort] = useState({});
  const [search, setSearch] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(false);

  const apiRef = useGridApiRef();

  // search
  const [searchInput, setSearchInput] = useState("");

  // Dialog state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedConnectLogId, setSelectedConnectLogId] = useState(null);

  useEffect(() => {
    setTitle("VPNConnectLog Management")

    const abortController = new AbortController()

    const getAllConnectLogs = async () => {
      setLoading(true)
      try {
        const response = await axiosPrivate.get('/api/vpnManagement/getvpnconnectlog', {
          params: { page: page + 1, pageSize, sort: JSON.stringify(sort), search },
          signal: abortController.signal
        })
        dispatch({ type: 'SET_VPNCONNECTLOG', payload: response.data })
        setTotalCount(response.data.totalCount || response.data.total || 0)
      } catch (err) {
        // cancelled requests and errors are ignored here
      } finally {
        if (!abortController.signal.aborted) setLoading(false)
      }
    }

    if (auth) {
      getAllConnectLogs()
    }

    return () => {
      abortController.abort()
    }
  }, [auth, axiosPrivate, dispatch, page, pageSize, search, setTitle, sort, refresh]);

  // Wait until the user stops typing, then search all logs (from the first page)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(query.trim())
      setPage(0)
    }, 400)

    return () => clearTimeout(timer)
  }, [query])

  const handleDelete = (id) => {
    // Logic for deleting the row with the given id
    console.log(`Delete row with id: ${id}`);
    handleOpenDialog(id);
  };

  // Open delete confirmation dialog
  const handleOpenDialog = (connectLogId) => {
    setSelectedConnectLogId(connectLogId);
    setOpenDeleteDialog(true);
  };

  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedConnectLogId(null);
  };

  const handleDeleteConnectLog = async () => {
    try {
      const response = await axiosPrivate.delete(`/api/vpnManagement/deletevpnconnectlog/${selectedConnectLogId}`);
      dispatch({ type: 'DELETE_VPNCONNECTLOG', payload: response.selectedConnectLogId });
      
      setRefresh(prev => prev + 1);

      handleCloseDeleteDialog();
    } catch (error) {
      console.error("Failed to delete the connectLog:", error);
    }
  };
  
  const handleRefresh = async (log_id) => {
    console.log(`Refreshing data for ID: ${log_id}`);

    try {
      const response = await axiosPrivate.patch(`/api/vpnManagement/updatevpnconnectlog/${log_id}`);
      dispatch({ type: 'UPDATE_VPNCONNECTLOG', payload: response.data });
    } catch (err) {
      // Optionally handle error
      console.error('Failed to update connectLog:', err);
    }
  };

  const formatErrorCode = (value) => {
    switch (value) {
      case 0: 
        return "CONNECTION";
      case 1:
        return "FB_NOTCOMPLETE";
      case 2:
        return "BADKEY";
      case 102:
        return "SERVERID_NULL";
      case 103:
        return "RELOAD_FAIL1";
      case 201:
        return "VPN_CONNECTED";
      case 202:
        return "VPN_CONNECTION_FAILED";
      default:
        return value;
    }
  };

  // data columns
  const columns = [
    {
      field: "_id",
      headerName: "ID",
      flex: 0.3,
      renderCell: (params) => (
        <div>
            {params.value.slice(-5)}
        </div>
      ),
    },
    {
      field: "device_id",
      headerName: "DEV ID",
      flex: 0.5,
      renderCell: (params) => (
        <div>
            {params.value.slice(-12)}
        </div>
      ),
    },
    {
      field: "country",
      headerName: "County",
      renderCell: (params) => (
        <div>
          {params.value ? params.value : (
            <Tooltip title="Refresh">
              <IconButton
                color="secondary"
                size="small"
                onClick={() => handleRefresh(params.row._id)}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },   
    {
      field: "isp",
      headerName: "ISP",
      flex: 0.6,
      renderCell: (params) => (
        <div>
          {params.value ? params.value : (
            <Tooltip title="Refresh">
              <IconButton
                color="secondary"
                size="small"
                onClick={() => handleRefresh(params.row._id)}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      field: "device_model",
      headerName: "MODEL",
    },
    {
      field: "os_version",
      headerName: "OS Version",
    },
    {
      field: "app_version",
      headerName: "App Version",
    },
    {
      field: "app_sig",
      headerName: "Sig",
      flex: 0.2,
    },
    {
      field: "server_ip",
      headerName: "IP",
      flex: 0.5
    },
    {
      field: "server_provider",
      headerName: "Provider",
    },
    {
      field: "error_code",
      headerName: "Status",
      flex: 0.6,
      renderCell: (params) => {        
        const formattedValue = formatErrorCode(params.value); 
        const textColor =
          params.value === 202 || params.value === 102 ? 'error.main' : // Error theme for 202 and 102
          params.value === 201 ? 'success.main' : // Success theme for 201
          'inherit';
    
        return (
          <Box
            sx={{
              color: textColor, 
              padding: '2px',
              borderRadius: '4px',
              width: '100%',
              textAlign: 'center',
            }}
          >
            {formattedValue}
          </Box>
        );
      },
    },    
    {
      field: "log_ip",
      headerName: "Log IP",
      flex: 0.5
    },
    {
      field: "log_time",
      headerName: "Connect Time",
      flex: 0.6,
      renderCell: (params) => {
        return format(new Date(params.value), 'yyyy-MM-dd HH:mm:ss');
      }
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.3,
      valueFormatter: (params) => `${params === 0 ? 'SUCC' : 'FAIL'}`,
    },    
    {
      field: "actions",
      type: "actions",
      flex: 0.3,
      renderCell: (params) => (
        <div>          
          <Tooltip title="Delete">
            <IconButton
              color="secondary"
              size="small"
              onClick={() => handleDelete(params.row._id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </div>
      ),
    }
  ];

  const rows = vpnConnectLogs?.items || []

  return (
    <Box sx={PAGE_SX}>
      <FlexBetween 
        sx={{
          flexDirection: {
            xs: 'column',
            sm: 'row',
          },
            alignItems: {
            xs: 'flex-start',
            sm: 'center',
          },
          gap: '1rem',
        }}
      >
        {/* Header */}
        <Header title="VPNCONNECT LOG" subtitle="Connection Logs Management" />
        <PageBanner />

        <FlexBetween>       
            {/* Search */}
            <TextField
              label="Search"
              placeholder="Device, country, ISP, IP, error..."
              variant="standard"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{
                mb: "0.5rem",
                mr: "1rem",
                width: "15rem",
              }}
              InputProps={{
                endAdornment: (
                <InputAdornment position="end">
                  <Search sx={{ color: theme.palette.secondary[200] }} />
                </InputAdornment>
                ),
              }}
            />
        </FlexBetween>
      </FlexBetween>
      {/* Content */}
      <Box
        mt="1rem"
        flex={1}
        minHeight="400px"
        backgroundColor={theme.palette.background.alt}
        border={gridBorder}
        borderRadius="0.55rem"
        overflow="hidden"
      >
        {/* Grid Table */}
        <DataGrid
          loading={loading}
          getRowId={(row) => row._id}
          rows={rows}
          columns={columns}
          rowCount={totalCount}
          pageSizeOptions={[20, 50, 100]}
          pagination
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          sortingMode="server"
          onPaginationModelChange={(newModel) => {
            setPage(newModel.page);
            setPageSize(newModel.pageSize);
          }}
          onSortModelChange={(newSortModel) => setSort(newSortModel[0] || {})}
          components={{
            Toolbar: DataGridCustomToolbar,
            ColumnMenu: CustomColumnMenu,
            Pagination: CustomPagination,
          }}
          componentsProps={{
            pagination: {
              apiRef,
              count: totalCount,
              page,
              pageSize,
              onPageChange: setPage,
              onPageSizeChange: setPageSize,
            },
            toolbar: { searchInput, setSearchInput, setSearch },
          }}
          sx={gridSx}
        />
      </Box>

      {!loading && !rows.length && search && (
        <Typography color="text.secondary" mt={1}>No matching results found...</Typography>
      )}

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        title="Delete connection log?"
        handleClose={handleCloseDeleteDialog}
        handleDelete={handleDeleteConnectLog}
      /> 
    </Box>
  );
};

export default VPNConnectLog;

